import * as Sentry from '@sentry/react'

import { ErrorMonitoring } from '@/core/services/error-monitoring'
import { Decamelize } from '@helpers/object'
import { Session } from '@/sessions/models'
import { config } from '@/offline/config'
import { SW_CACHED_ROUTES, TWYLL_OFFLINE } from '@/offline/constants'
import { User, userSchema } from '@/core/models/users/user-models'
import { parseResponse } from '@helpers/parseResponse'

import { BackgroundSyncManagerInterface } from '../interfaces/BackgroundSyncManagerInterface'
import { LocalDataManagerInterface } from '../interfaces/LocalDataManagerInterface'
import { SymmetricKeyManagerInterface } from '../interfaces/SymmetricKeyManagerInterface'
import {
  SYNC_EVENT_TYPES,
  SyncEventBusManagerInterface,
} from '../interfaces/SyncEventBusManagerInterface'
import { Stores } from '../models'
import { syncUpcomingSessions } from '../services/syncUpcomingSessions'

import SyncEventBusManager from './SyncEventBusManager'

type SessionSnakeCase = Decamelize<Session>

const ONE_HOUR_IN_MILLIS = 60 * 60 * 1000
const RETRYABLE_STATUS_CODES = new Set([408, 425, 429, 500, 502, 503, 504])

const fetchAfterTimeout = (
  url: string,
  timeout: number,
  fetchOptions: RequestInit = { cache: 'reload' },
): Response | PromiseLike<Response> => {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      fetch(url, fetchOptions).then(resolve).catch(reject)
    }, timeout)
  })
}

const fetchWithRetry = async (url: string) => {
  const retryAttempts = 3
  let count = 0
  let timeout = 1000
  let resp

  try {
    resp = await fetch(url)
  } catch {
    timeout *= 2
    count++
  }

  // retry if fetch throws
  while (!resp && count < retryAttempts) {
    try {
      resp = await fetchAfterTimeout(url, timeout)
    } catch {
      timeout *= 2
      count++
    }
  }

  if (!resp) {
    throw new Error(`Failed to fetch after ${retryAttempts} attempts`)
  }

  // retry if the request failed due to a retryable status code
  while (RETRYABLE_STATUS_CODES.has(resp.status) && count < retryAttempts) {
    resp = await fetchAfterTimeout(url, timeout)

    timeout *= 2
    count++
  }

  return resp
}

export default class BackgroundSyncManager
  implements BackgroundSyncManagerInterface
{
  private readonly interval: number
  private intervalId: number | null
  private syncEventBusManager: SyncEventBusManagerInterface
  private symmetricKeyManager: SymmetricKeyManagerInterface
  private localDataManager: LocalDataManagerInterface
  private user: User | null = null

  /**
   * Instantiates a new BackgroundSyncManager instance
   * @param localDataManager The manager for interacting with indexDB
   * @param symmetricKeyManager The manager for retrieving and storing a symmetric key
   * @param interval The interval at which to sync with the backend
   */
  constructor(
    localDataManager: LocalDataManagerInterface,
    symmetricKeyManager: SymmetricKeyManagerInterface,
    interval = 1000 * 60 * 60,
  ) {
    this.interval = interval
    this.intervalId = null
    this.syncEventBusManager = new SyncEventBusManager()
    this.symmetricKeyManager = symmetricKeyManager
    this.localDataManager = localDataManager

    this.setupEventListeners()
  }

  /**
   * Start the periodic sync of data with the backend. Called as part of the
   * offline app initialization process on every page load.
   */
  async startBackgroundSync() {
    if (!this.isUserLoggedIn()) {
      await this.localDataManager.deleteData(
        Stores.SymmetricKey,
        config.SYMMETRIC_KEY_ID,
      )
      window.location.assign('/accounts/login/')
      return
    }

    this.syncEventBusManager.dispatch(
      new CustomEvent(SYNC_EVENT_TYPES.START, {
        detail: { message: 'starting background sync' },
      }),
    )

    const oneHourAgo = Date.now() - ONE_HOUR_IN_MILLIS

    let lastUpdatedAt = localStorage.getItem('lastUpdatedAt')
      ? Number(localStorage.getItem('lastUpdatedAt'))
      : 0

    if (!lastUpdatedAt || lastUpdatedAt < oneHourAgo) {
      try {
        await this.doInitialSync()
        const now = Date.now()
        await this.localDataManager.setLastUpdatedAt(now)
        localStorage.setItem('lastUpdatedAt', String(now))
        lastUpdatedAt = now
        this.syncEventBusManager.dispatch(
          new CustomEvent(SYNC_EVENT_TYPES.SUCCESS, {
            detail: { message: 'background sync completed successfully' },
          }),
        )
      } catch (e) {
        this.syncEventBusManager.dispatch(
          new CustomEvent(SYNC_EVENT_TYPES.ERROR, {
            detail: { error: e },
          }),
        )
      }
    } else if (document.referrer.includes('/sessions/waiting')) {
      try {
        await this.syncISEEvents()
        this.syncEventBusManager.dispatch(
          new CustomEvent(SYNC_EVENT_TYPES.SUCCESS, {
            detail: { message: 'background sync completed successfully' },
          }),
        )
      } catch (e) {
        this.syncEventBusManager.dispatch(
          new CustomEvent(SYNC_EVENT_TYPES.ERROR, {
            detail: { error: e },
          }),
        )
      }
    } else {
      this.syncEventBusManager.dispatch(
        new CustomEvent(SYNC_EVENT_TYPES.SUCCESS, {
          detail: { message: 'background sync completed successfully' },
        }),
      )
    }

    setTimeout(
      () => {
        // start background sync
        this.intervalId = window.setInterval(async () => {
          this.syncEventBusManager.dispatch(
            new CustomEvent(SYNC_EVENT_TYPES.START, {
              detail: { message: 'starting background sync' },
            }),
          )

          try {
            await this.doHourlySync()
          } catch (er) {
            Sentry.captureException(er)
            this.syncEventBusManager.dispatch(
              new CustomEvent(SYNC_EVENT_TYPES.ERROR, {
                detail: { error: er },
              }),
            )
          }

          this.syncEventBusManager.dispatch(
            new CustomEvent(SYNC_EVENT_TYPES.SUCCESS, {
              detail: { message: 'background sync completed successfully' },
            }),
          )
        }, this.interval)
      },
      Date.now() - (lastUpdatedAt + ONE_HOUR_IN_MILLIS),
    )
  }

  /**
   * Stop the periodic background sync of data with the backend
   */
  stopBackgroundSync() {
    if (this.intervalId) {
      clearInterval(this.intervalId)
      this.intervalId = null
    } else {
      console.warn(
        'attempted to stop background sync when background sync was not running',
      )
    }
  }

  /**
   * Sync the sessions page and all ISEs it links to.
   * @returns Promise<void>
   */
  private async syncISE() {
    try {
      const upcomingSessions = await syncUpcomingSessions()

      const linksISE =
        upcomingSessions?.sessions.reduce<string[]>(
          (acc, { session_type, uuid }: SessionSnakeCase) => {
            if (session_type === 'session') {
              acc.push(`/sessions/${uuid}`)
            }
            return acc
          },
          [],
        ) || []

      await this.clearOldISELinks(linksISE)

      await this.fetchAndCache('ISE', linksISE)
    } catch (e) {
      if (e instanceof Error) {
        throw new Error(e.message, { cause: e })
      }

      throw e
    }
  }

  private async clearOldISELinks(newISElinks: string[]) {
    const cache = await caches.open(TWYLL_OFFLINE)
    const keys = await cache.keys()

    const oldISELinks = keys.reduce<string[]>((acc, { url }) => {
      if (
        url.match(
          /\/sessions\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/,
        )
      ) {
        acc.push(url.replace(location.origin, ''))
      }

      return acc
    }, [])

    oldISELinks.forEach((link) => {
      if (!newISElinks.includes(link)) {
        cache.delete(link)
      }
    })
  }

  /**
   * Send a POST request to create a new session record in db which was created in offline mode
   */
  private async syncNewSession() {
    const newSessionData = localStorage.getItem('new-session-to-post')

    if (newSessionData) {
      try {
        const { clientId, formattedData: payload } = JSON.parse(newSessionData)
        const response = await fetch(
          new URL(
            `${window.location.origin}/api/offline/client/${clientId}/new_session/`,
          ),
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': window.CSRFTOKEN,
              Accept: 'application/json',
            },
            body: JSON.stringify(payload),
          },
        )
        if (response.status !== 201) {
          throw new Error(`failed to create a new offline session`)
        } else {
          localStorage.removeItem('new-session-to-post')
        }
      } catch (e) {
        if (e instanceof Error) {
          const error = new Error(e.message, { cause: e })
          error.name = `Error during quick session sync: ${error.name}`

          ErrorMonitoring.captureException(error)
        }

        throw e
      }
    }
  }

  /**
   * Sync the symmetric key used for encrypting offline events
   */
  private async syncSymmetricKey() {
    // DBW 6/21/24 -- this returns the key, but we only need to refresh the cache,
    // so we drop the return value on the floor
    try {
      await this.symmetricKeyManager.fetchAndCache()
    } catch (e) {
      this.syncEventBusManager.dispatch(
        new CustomEvent(SYNC_EVENT_TYPES.ERROR, {
          detail: { error: e },
        }),
      )
      Sentry.captureException(e)
    }
  }

  private async syncClients() {
    const {
      policies: { canListClinics },
      roles,
    } = await this.getUser()

    if (canListClinics) {
      await this.fetchAndCache('All Clients page', [
        SW_CACHED_ROUTES.allClients,
      ])
    } else if (roles?.includes('Therapist') || roles?.includes('Supervisor')) {
      await this.fetchAndCache('Clients page', [SW_CACHED_ROUTES.myClients])
    } else {
      console.warn('User does not have permission to sync any clients page')
    }
  }

  private async getUser(): Promise<User> {
    if (this.user !== null) {
      return this.user
    }

    try {
      const response = await fetchWithRetry(SW_CACHED_ROUTES.user)
      if (!response.ok) {
        throw new Error('Failed to fetch user permissions')
      }
      const userData = await response.json()

      this.user = parseResponse(userSchema, userData)
    } catch (er) {
      throw new Error('Failed to fetch user permissions', { cause: er })
    }

    return this.user
  }

  /**
   * Fetch and cache (via sw registerRoute) a list of urls
   * @param pageName The name of the page we're fetching and caching for
   * @param resources The list of urls to fetch
   * @returns Promise<void>
   */
  private async fetchAndCache(pageName: string, resources: string[]) {
    const responses: Response[] = []

    // for of - for making all requests sequentially
    // to avoid overloading server
    for (const resource of resources) {
      responses.push(await fetchWithRetry(resource))
    }

    if (responses.some((response) => response.status >= 400)) {
      this.syncEventBusManager.dispatch(
        new CustomEvent(SYNC_EVENT_TYPES.ERROR, {
          detail: { message: `Failed to sync ${pageName}` },
        }),
      )
      return
    }
  }

  private isUserLoggedIn() {
    return !!getCookieValue('verification_token')
  }

  private async doInitialSync() {
    await this.syncSymmetricKey()
    await this.syncNewSession()
    await this.syncISEEvents()

    await this.syncClients()

    const {
      policies: { canListSessions },
    } = await this.getUser()

    if (canListSessions) {
      await this.syncISE()

      await this.fetchAndCache('Offline Session', [
        SW_CACHED_ROUTES.address,
        SW_CACHED_ROUTES.treatmentPlans,
      ])
    } else {
      console.warn('User does not have permission to sync session pages.')
    }
  }

  private async doHourlySync() {
    await this.syncSymmetricKey()

    const {
      policies: { canListSessions },
    } = await this.getUser()

    if (canListSessions) {
      await this.syncISE()

      await this.fetchAndCache('Offline Session', [
        SW_CACHED_ROUTES.address,
        SW_CACHED_ROUTES.treatmentPlans,
      ])
    } else {
      console.warn('User does not have permission to sync session pages.')
    }
  }

  private async syncISEEvents() {
    await navigator.locks.request('ise-events-sync', async () => {
      try {
        await this.localDataManager.initDB()
        await this.localDataManager.removeExpiredData()
        await this.localDataManager.drainQueue()
      } catch (e) {
        Sentry.captureException(e)
      }
    })
  }

  private setupEventListeners() {
    this.syncEventBusManager.addEventListener(
      SYNC_EVENT_TYPES.MANUAL,
      async () => {
        this.syncEventBusManager.dispatch(
          new CustomEvent(SYNC_EVENT_TYPES.START, {
            detail: { message: 'starting background sync' },
          }),
        )

        try {
          await this.doInitialSync()
        } catch (e) {
          this.syncEventBusManager.dispatch(
            new CustomEvent(SYNC_EVENT_TYPES.ERROR, {
              detail: { error: e },
            }),
          )
        }

        this.syncEventBusManager.dispatch(
          new CustomEvent(SYNC_EVENT_TYPES.SUCCESS, {
            detail: { message: 'background sync completed successfully' },
          }),
        )
      },
    )

    this.syncEventBusManager.addEventListener(
      SYNC_EVENT_TYPES.RECONNECT,
      async () => {
        this.syncEventBusManager.dispatch(
          new CustomEvent(SYNC_EVENT_TYPES.START, {
            detail: { message: 'starting sessions sync' },
          }),
        )

        try {
          await this.syncISEEvents()
        } catch (e) {
          this.syncEventBusManager.dispatch(
            new CustomEvent(SYNC_EVENT_TYPES.ERROR, {
              detail: { error: e },
            }),
          )
        }

        this.syncEventBusManager.dispatch(
          new CustomEvent(SYNC_EVENT_TYPES.SUCCESS, {
            detail: { message: 'sessions sync completed successfully' },
          }),
        )
      },
    )
  }
}

function getCookieValue(cookieName: string) {
  return document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${cookieName}=`))
    ?.split('=')[1]
}
